import {
  ButtonsBloc,
  FGCustomPanel,
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  AffixRequestApi,
  EPlatform,
  ERequestState,
  KennelImportRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
  RequestInfoDto,
} from "../../../../../api";
import {
  CancelButton,
  FGCountrySelect,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../../components";
import { useAuth, useDialog, useEventsContext } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";
import { RequestBreedTransientSelection } from "../../../request/item/RequestBreedTransientSelection";
import { RequestTransientPersonSelection } from "../../../request/item/RequestTransientPersonSelection";

export interface IKennelImportRequestDetailProps {
  kennelImportRequestId: number;
  editMode: boolean;
  requestInfo: RequestInfoDto;
}

export const KennelImportRequestDetail: React.FunctionComponent<IKennelImportRequestDetailProps> = ({
  kennelImportRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(AffixRequestApi);
  const personApi = useApiService(PersonApi);
  const [selectorErrors, setselectorErrors] = React.useState("");
  const { user } = useAuth();

  const { openNewTab } = useWindow();
  const { showDialogPromise } = useDialog();

  const [kennelTypes, ktloading] = useReferential((a) => a.getKennelTypes(), false, []);

  const [ownerIdsBeforeEdition, setOwnerIdsBeforeEdition] = React.useState([]);
  const [breedIdsBeforeEdition, setBreedIdsBeforeEdition] = React.useState([]);
  // =========================== DATA MANIPULATION ==================================================

  const { data, loading, saving, saveItem, refresh } = useCrudApi<KennelImportRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = kennelImportRequestId) =>
          id > 0
            ? api.getImportKennelRequest(id)
            : new KennelImportRequestDto({
                transientOwnerIds: [],
                transientBreedIds: [],
                request: new RequestDto({
                  status: ERequestState.REQUESTED,
                  remark: "",
                  requestDate: new Date(),
                }),
              }).toJSON(),
        saveApiFn: (d: KennelImportRequestDto) => {
          const id = api.saveKennelImportRequest(d);
          const updatedOwnerIds = [...ownerIdsBeforeEdition];
          d.transientOwnerIds?.forEach((ownerId) => {
            if (!updatedOwnerIds.includes(ownerId)) {
              updatedOwnerIds.push(ownerId);
            }
          });
          const updatedBreedIds = [...breedIdsBeforeEdition];
          d.transientOwnerIds?.forEach((breedId) => {
            if (!updatedBreedIds.includes(breedId)) {
              updatedBreedIds.push(breedId);
            }
          });
          setOwnerIdsBeforeEdition(updatedOwnerIds);
          setBreedIdsBeforeEdition(updatedBreedIds);
          return id;
        },
        onSavedRoute: (d) => `${ERoutes.affixRequest}/${d.kennelImportRequestId}/detail/edit/kennelImport`,
      }),
      [api, breedIdsBeforeEdition, kennelImportRequestId, ownerIdsBeforeEdition]
    ),
    [kennelImportRequestId]
  );

  const computeError = React.useCallback(
    (ids) => {
      if (kennelImportRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
        ids.forEach(async (element) => {
          const result = await personApi.isPersonBelgian(element);
          if (!result.value) {
            setselectorErrors(t(ETLCodes.OneOrMoreOwnersNotBelgian));
            return;
          }
        });
        ids.forEach(async (element) => {
          const result = await personApi.isKennelOwner(element, 0, new Date(), null);
          if (result.value) {
            setselectorErrors(t(ETLCodes.OneOrMorPersonAlreadyOwnsAKennel));
            return;
          }
        });
        setselectorErrors("");
        return;
      }
    },
    [data?.request?.status, kennelImportRequestId, personApi, t]
  );

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      if (selectorErrors !== "") {
        return;
      }

      let isBanned = false;
      if (kennelImportRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [kennelImportRequestId, personApi, saveItem, selectorErrors, showDialogPromise, t]
  );

  // =========================== EVENT MANAGER ==================================================
  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  // =========================== FORM ==================================================
  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      nameOffer: Yup.string().required(t(ETLCodes.Required)),
      nameOfferType: Yup.string().nullable().required(t(ETLCodes.Required)),
      countryId: Yup.number().nullable().required(t(ETLCodes.Required)),
      transientOwnerIds: Yup.array().of(
        Yup.number().test("is-begian", t(ETLCodes.AtLeastOneOwnerIsNotBelgian), async function (value) {
          if (kennelImportRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const result = await personApi.isPersonBelgian(value);
            return result.value;
          }
          return true;
        })
      ),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-begian", t(ETLCodes.IsNotBelgian), async function (value) {
            if (kennelImportRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isPersonBelgian(value);
              return result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (kennelImportRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [data, kennelImportRequestId, personApi, t]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const statusEditable = React.useMemo(() => {
    if (data) {
      return (
        data?.request?.status === ERequestState.REQUESTED ||
        data?.request?.status === ERequestState.AWAITING_APPLICANT_REACTION ||
        data?.request?.status === ERequestState.BILLED ||
        data?.request?.status === ERequestState.PAID
      );
    }
    return false;
  }, [data]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.affixRequest)}
        saving={saving || saveChecking}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
      >
        <FGListen field="transientOwnerIds" onChanged={computeError} />
        <FieldSet
          title={t(ETLCodes.ImportKennelRequest)}
          rightElement={
            kennelImportRequestId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            )
          }
        >
          <FieldGroup columns={2}>
            <div>
              <FGSRSHDateMaskInput
                name="request.requestDate"
                label={t(ETLCodes.RequestDate)}
                requiredMark
                readonly={!statusEditable || data?.request?.isBlocked}
              />
              <FGPeopleSearchInput
                name="request.personId"
                label={t(ETLCodes.Contact)}
                onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
                readonly={!statusEditable || data?.request?.isBlocked}
                requiredMark
                showWarning={true}
              />
            </div>
            <FGTextAreaInput label={t(ETLCodes.Remark)} name="request.remark" growVertically rows={6} />
          </FieldGroup>
        </FieldSet>

        <FieldSet title={t(ETLCodes.FileAffix)}>
          <FieldGroup columns={2}>
            <FGTextInput
              name="nameOffer"
              trim={true}
              label={t(ETLCodes.NameOffer)}
              maxLength={29}
              forceCase={"upper"}
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            <FGSelectInput
              name="nameOfferType"
              label={t(ETLCodes.Affix)}
              items={kennelTypes}
              loading={ktloading}
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            <FGCountrySelect
              name="countryId"
              label={t(ETLCodes.CountryOfOrigin)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.country}/${item}/detail/view`)}
              readonly={!statusEditable || data?.request?.isBlocked}
            />
          </FieldGroup>
        </FieldSet>
        {user.platform === EPlatform.PORTAL ? null : (
          <FieldSet title={t(ETLCodes.Owners)}>
            <FGCustomPanel>
              {({ formik }) => (
                <>
                  <RequestTransientPersonSelection
                    linkedIds={formik?.values?.transientOwnerIds || null}
                    onLinkedIdsChanged={(ids) => formik?.setFieldValue("transientOwnerIds", ids)}
                    editMode={editMode && !data?.request?.isBlocked && statusEditable}
                    loading={loading}
                    errors={selectorErrors}
                  />
                </>
              )}
            </FGCustomPanel>
          </FieldSet>
        )}
        <FieldSet title={t(ETLCodes.Breeds)}>
          <FGCustomPanel>
            {({ formik }) => (
              <>
                <RequestBreedTransientSelection
                  linkedIds={formik?.values?.transientBreedIds || null}
                  onLinkedIdsChanged={(ids) => formik?.setFieldValue("transientBreedIds", ids)}
                  editMode={editMode && !data?.request?.isBlocked && statusEditable}
                  loading={loading}
                />
              </>
            )}
          </FGCustomPanel>
        </FieldSet>
        {(data?.request?.status === ERequestState.TO_EVALUATE_FCI ||
          data?.request?.status === ERequestState.EXECUTED) && (
          <FieldGroup columns={2} fieldsetProps={{ title: t(ETLCodes.FciNumber) }}>
            <FGTextInput name="fciNumber" label={t(ETLCodes.FciNumber)} />
          </FieldGroup>
        )}
        {data?.request?.status === ERequestState.EXECUTED && (
          <InlineButtonContainer>
            <div />
            <ButtonsBloc style={{ float: "right" }}>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.affixRequest}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
      </SmallFormGenerator>
    </>
  );
};
